import { useState } from 'react';
import { ArrowUp, ArrowDown, X, Plus, Shuffle } from 'lucide-react';
import { getAllProducts, getCategory } from '../../../data/catalog';
import { useSiteConfig, saveSiteConfig, getFeaturedList } from '../../../utils/siteConfig';
import { isProductVisible } from '../../../utils/availability';
import { useQueue } from '../../../context/QueueContext';

export default function FeaturedSection({ flash }) {
  const { user } = useQueue();
  const cfg = useSiteConfig();
  const [list, setList] = useState(() => getFeaturedList(cfg).map((f) => ({ ...f })));
  const [mode, setMode] = useState(cfg.featuredMode === 'manual' ? 'manual' : 'auto');
  const [count, setCount] = useState(() => Math.max(1, Math.round(Number(cfg.featuredCount) || 12)));
  const [addSlug, setAddSlug] = useState('');

  const move = (i, dir) => {
    const next = [...list];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setList(next);
  };

  const add = () => {
    if (!addSlug || list.some((f) => f.slug === addSlug)) return;
    setList([...list, { slug: addSlug, active: true }]);
    setAddSlug('');
  };

  const save = async () => {
    try {
      await saveSiteConfig(
        { featured: list, featuredMode: mode, featuredCount: Math.max(1, Math.round(Number(count) || 12)) },
        user,
        'Alterou os tecidos em destaque da Home.'
      );
      flash('Tecidos em destaque salvos.');
    } catch (e) {
      flash(e.message || 'Falha ao salvar os tecidos em destaque.');
    }
  };

  const available = getAllProducts().filter((p) => !list.some((f) => f.slug === p.slug));
  const withPhoto = getAllProducts().filter((p) => p.variants?.[0]?.image || p.image).length;

  return (
    <section className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="pers-featured-section">
      <h3 className="font-serif text-xl text-navy">Tecidos em Destaque</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Controla a área “Destaques” da Home. O destaque referencia o tecido real do catálogo — se ele ficar indisponível, some da Home automaticamente.
      </p>

      <div className="mt-4 grid grid-cols-2 gap-2" data-testid="pers-feat-mode">
        <button
          type="button"
          onClick={() => setMode('auto')}
          aria-pressed={mode === 'auto'}
          data-testid="pers-feat-mode-auto"
          className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-xs font-bold ${mode === 'auto' ? 'border-navy bg-navy text-white' : 'border-navy/20 bg-white text-navy hover:bg-navy-light'}`}
        >
          <Shuffle className="h-4 w-4" /> ROTAÇÃO AUTOMÁTICA
        </button>
        <button
          type="button"
          onClick={() => setMode('manual')}
          aria-pressed={mode === 'manual'}
          data-testid="pers-feat-mode-manual"
          className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-xs font-bold ${mode === 'manual' ? 'border-navy bg-navy text-white' : 'border-navy/20 bg-white text-navy hover:bg-navy-light'}`}
        >
          LISTA MANUAL
        </button>
      </div>

      {mode === 'auto' ? (
        <div className="mt-4 rounded-2xl border border-navy/10 bg-navy-light/30 p-4" data-testid="pers-feat-auto-panel">
          <p className="text-sm text-navy">
            A Home sorteia os destaques <strong>uma vez por dia</strong> entre todos os tecidos visíveis que têm foto
            ({withPhoto} no catálogo agora) — todos os visitantes veem a mesma seleção no dia e ela muda a cada dia,
            girando o catálogo inteiro. Tecido novo cadastrado entra no sorteio sozinho; indisponível ou sem foto sai sozinho.
          </p>
          <label htmlFor="pers-feat-count" className="mt-3 block text-xs font-semibold text-navy">
            Quantidade de destaques na Home
          </label>
          <input
            id="pers-feat-count"
            type="number"
            min={1}
            max={60}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            data-testid="pers-feat-count"
            className="mt-1 h-12 w-28 rounded-xl border border-input bg-white px-4 text-sm font-medium outline-none focus:border-navy"
          />
          {list.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              Sua lista manual ({list.length} tecido(s)) fica guardada: se um dia você voltar ao modo “Lista manual”, ela é usada de novo.
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="mt-4 flex gap-2">
            <label htmlFor="pers-feat-add-select" className="sr-only">Adicionar tecido aos destaques</label>
            <select id="pers-feat-add-select" data-testid="pers-feat-add-select" value={addSlug} onChange={(e) => setAddSlug(e.target.value)} className="h-12 w-full rounded-xl border border-input bg-white px-4 text-sm font-medium outline-none focus:border-navy">
              <option value="">Adicionar tecido…</option>
              {available.map((p) => (
                <option key={p.slug} value={p.slug}>{p.name}</option>
              ))}
            </select>
            <button type="button" onClick={add} disabled={!addSlug} data-testid="pers-feat-add-button" className="flex h-12 shrink-0 items-center gap-2 rounded-xl bg-navy px-4 text-xs font-bold text-white hover:bg-navy-hover disabled:opacity-40">
              <Plus className="h-4 w-4" /> ADICIONAR
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {list.map((f, i) => {
              const p = getAllProducts().find((x) => x.slug === f.slug);
              if (!p) return null;
              const visible = isProductVisible(p);
              const cat = getCategory(p.categorySlug);
              return (
                <div key={f.slug} className={`rounded-2xl border p-3 ${f.active ? 'border-navy/10 bg-white' : 'border-navy/10 bg-navy-light/30 opacity-70'}`} data-testid={`pers-feat-row-${f.slug}`}>
                  <div className="flex items-center gap-3">
                    <img src={p.variants[0]?.image} alt={p.name} className="h-14 w-14 shrink-0 rounded-lg object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium leading-tight">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{cat?.name}</p>
                      {!visible && (
                        <span className="mt-1 inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`pers-feat-unavailable-${f.slug}`}>
                          Indisponível no catálogo — não aparece na Home
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Mover para cima" data-testid={`pers-feat-up-${f.slug}`} className="flex h-10 w-10 items-center justify-center rounded-xl border border-navy/20 text-navy hover:bg-navy-light disabled:opacity-30">
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} aria-label="Mover para baixo" data-testid={`pers-feat-down-${f.slug}`} className="flex h-10 w-10 items-center justify-center rounded-xl border border-navy/20 text-navy hover:bg-navy-light disabled:opacity-30">
                      <ArrowDown className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => setList(list.map((x, xi) => (xi === i ? { ...x, active: !x.active } : x)))} aria-pressed={f.active} data-testid={`pers-feat-toggle-${f.slug}`} className={`flex h-10 items-center rounded-xl border px-4 text-xs font-semibold ${f.active ? 'border-emerald-500/50 text-emerald-700 hover:bg-emerald-50' : 'border-amber-400/60 text-amber-700 hover:bg-amber-50'}`}>
                      {f.active ? 'ATIVO' : 'INATIVO'}
                    </button>
                    <button type="button" onClick={() => setList(list.filter((x) => x.slug !== f.slug))} data-testid={`pers-feat-remove-${f.slug}`} className="flex h-10 items-center gap-2 rounded-xl border border-red-300 px-4 text-xs font-semibold text-red-700 hover:bg-red-50">
                      <X className="h-4 w-4" /> REMOVER
                    </button>
                  </div>
                </div>
              );
            })}
            {list.length === 0 && (
              <p className="rounded-2xl border border-dashed border-navy/20 p-8 text-center text-sm text-muted-foreground" data-testid="pers-feat-empty">
                Nenhum tecido em destaque. A área “Destaques” ficará vazia na Home.
              </p>
            )}
          </div>
        </>
      )}

      <button type="button" onClick={save} data-testid="pers-featured-save" className="mt-4 w-full rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover sm:w-auto">
        SALVAR ALTERAÇÕES
      </button>
    </section>
  );
}
